import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { GamblingGameProps } from './GamblingHall';
import diceStyles from './DiceGame.module.css';
import styles from './ChuckALuck.module.css';

// ── Dice face helper ─────────────────────────────────────────────────────────
const FACE_DOTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 20], [75, 20], [25, 50], [75, 50], [25, 80], [75, 80]],
};

const DieFace: React.FC<{ value: number; rolling: boolean; settled: boolean; highlight?: boolean }> = (
  { value, rolling, settled, highlight }
) => (
  <div className={`${diceStyles.die} ${rolling ? diceStyles.dieRolling : ''} ${settled ? diceStyles.dieSettled : ''} ${highlight ? styles.dieHighlight : ''}`}>
    {(FACE_DOTS[value] ?? FACE_DOTS[1]).map(([x, y], i) => (
      <span key={i} className={`${diceStyles.pip} ${diceStyles.pipVisible}`} style={{ left: `${x}%`, top: `${y}%` }} />
    ))}
  </div>
);

// ── Mini die picker ───────────────────────────────────────────────────────────
const MINI_DOTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[28, 22], [72, 22], [28, 50], [72, 50], [28, 78], [72, 78]],
};

const MiniDie: React.FC<{ value: number; selected: boolean; onClick: () => void }> = ({ value, selected, onClick }) => (
  <button className={`${styles.miniDie} ${selected ? styles.miniDieSelected : ''}`} onClick={onClick} title={`Pick ${value}`}>
    {MINI_DOTS[value].map(([x, y], i) => (
      <span key={i} className={`${styles.miniPip} ${styles.miniPipVisible}`} style={{ left: `${x}%`, top: `${y}%` }} />
    ))}
  </button>
);

// ── Constants ────────────────────────────────────────────────────────────────
const ROLL_ANIM_MS = 1400;

// ── Component ────────────────────────────────────────────────────────────────
const ChuckALuck: React.FC<GamblingGameProps> = ({ mercenaryId, silver, remaining, nextRollAt, onResult }) => {
  const [bet, setBet] = useState(10);
  const [pick, setPick] = useState<number | null>(null);
  const [rolling, setRolling] = useState(false);
  const [diceValues, setDiceValues] = useState<[number, number, number]>([1, 1, 1]);
  const [matchedDice, setMatchedDice] = useState<[boolean, boolean, boolean]>([false, false, false]);
  const [result, setResult] = useState<{ win: boolean; message: string } | null>(null);
  const [cooldownDisplay, setCooldownDisplay] = useState<string | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shuffleRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cooldown ticker
  useEffect(() => {
    if (!nextRollAt) { setCooldownDisplay(null); return; }
    const tick = () => {
      const diff = nextRollAt.getTime() - Date.now();
      if (diff <= 0) { setCooldownDisplay(null); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.ceil((diff % 60000) / 1000);
      setCooldownDisplay(m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRollAt]);

  const scheduleResultDismiss = useCallback(() => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => {
      setResult(null);
      setMatchedDice([false, false, false]);
    }, 5000);
  }, []);

  const handlePlay = async () => {
    if (pick === null || rolling || cooldownDisplay || remaining <= 0) return;
    setRolling(true);
    setResult(null);
    setMatchedDice([false, false, false]);

    shuffleRef.current = setInterval(() => {
      setDiceValues([
        Math.ceil(Math.random() * 6) as 1,
        Math.ceil(Math.random() * 6) as 1,
        Math.ceil(Math.random() * 6) as 1,
      ]);
    }, 80);

    try {
      const res = await axios.post('/api/tavern/play-chuckaluck', { mercenaryId, bet, pick });
      setTimeout(() => {
        if (shuffleRef.current) { clearInterval(shuffleRef.current); shuffleRef.current = null; }
        const r1 = res.data.roll1 as number;
        const r2 = res.data.roll2 as number;
        const r3 = res.data.roll3 as number;
        setDiceValues([r1, r2, r3]);
        setMatchedDice([r1 === pick, r2 === pick, r3 === pick]);
        setRolling(false);
        setResult({ win: res.data.win, message: res.data.message });
        onResult(res.data.newSilver, res.data.remaining, res.data.nextRollAt ? new Date(res.data.nextRollAt) : null);
        scheduleResultDismiss();
      }, ROLL_ANIM_MS);
    } catch (err: any) {
      if (shuffleRef.current) { clearInterval(shuffleRef.current); shuffleRef.current = null; }
      setRolling(false);
      setResult({ win: false, message: err?.response?.data?.message || err?.response?.data?.error || 'Error placing bet.' });
      scheduleResultDismiss();
    }
  };

  const canRoll = !rolling && !cooldownDisplay && remaining > 0 && pick !== null && bet >= 1;

  return (
    <div className={styles.game}>
      <p className={diceStyles.description}>
        Pick a number 1–6. Three dice are cast, each matching die pays your bet. (1 match: x1, 2 matches: x2, 3 matches: x3).
      </p>

      {/* Number picker */}
      <div className={styles.pickerRow}>
        {[1, 2, 3, 4, 5, 6].map(n => (
          <MiniDie key={n} value={n} selected={pick === n} onClick={() => setPick(n)} />
        ))}
      </div>

      {/* Three dice */}
      <div className={`${diceStyles.diceArea} ${styles.threeDice}`}>
        <DieFace value={diceValues[0]} rolling={rolling} settled={!rolling && result !== null} highlight={matchedDice[0]} />
        <DieFace value={diceValues[1]} rolling={rolling} settled={!rolling && result !== null} highlight={matchedDice[1]} />
        <DieFace value={diceValues[2]} rolling={rolling} settled={!rolling && result !== null} highlight={matchedDice[2]} />
      </div>

      {/* Bet & roll controls */}
      <div className={diceStyles.betControls}>
        <label>Bet:</label>
        <input
          type="number"
          className={diceStyles.plainInput}
          value={bet || ''}
          placeholder="1"
          min={1}
          max={Math.min(silver, remaining)}
          onChange={e => { const raw = e.target.value; setBet(raw === '' ? 0 : Math.min(silver, remaining, parseInt(raw) || 0)); }}
        />
        <button className={diceStyles.rollButton} onClick={handlePlay} disabled={!canRoll}>
          {rolling           ? 'Rolling…'
            : cooldownDisplay  ? `Wait ${cooldownDisplay}`
            : remaining <= 0   ? 'Limit reached'
            : pick === null    ? 'Pick a number'
            : 'Roll!'}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className={result.win ? diceStyles.resultSuccess : diceStyles.resultError}>
          {result.message}
        </div>
      )}
    </div>
  );
};

export default ChuckALuck;
